import { useState, useContext, useEffect } from 'react';
import HeaderBackground from '../components/headerbackground.js';
import getAPI from '../functions/API/getAPI.js';
import { toast } from '../lib/toast.js';
import GlobalContext from '../context/GlobalContext.js';
import { useParams, useOutletContext } from 'react-router-dom';
import { FileText, ChevronRight } from 'lucide-react';

export default function Documents() {
    const [loading, setLoading] = useState(true);
    const { theme, themeCSS, settings } = useContext(GlobalContext);
    const primaryColor = settings?.primary_hex_colour || '#813468';
    const [documents, setDocuments] = useState([]);
    const [loadingDocuments, setLoadingDocuments] = useState({});
    const { id } = useParams();
    const { agreementId } = useOutletContext() || {};
    const navigate = useNavigate();

    // Use the fresh agreementId from the layout context, falling back to URL param
    const docId = agreementId || id;

    async function getDocuments() {
        setLoading(true);
        const res = await getAPI('documents/' + docId);
        if (res.response?.ok && Array.isArray(res.data)) {
            setDocuments(res.data);
        } else if (res.response?.ok && res.data?.documents) {
            setDocuments(res.data.documents);
        } else {
            setDocuments([]);
        }
        setLoading(false);
    }

    async function downloadDocument(docID) {
        setLoadingDocuments(prev => ({ ...prev, [docID]: true }));
        let chunk = 0;
        let data = [];
        let extension, fileName, maxChunks;

        const response = await getAPI(`documents/download/${docID}/${chunk}`, false, true);
        if (!response.response.ok) {
            toast.error('Error downloading document');
            setLoadingDocuments(prev => ({ ...prev, [docID]: false }));
            return;
        }

        maxChunks = parseInt(response.response.headers.get('X-Chunks'), 10);
        extension = response.response.headers.get('X-Extension');
        fileName = response.response.headers.get('X-File-Name');
        data.push(await response.response.arrayBuffer());
        chunk++;

        try {
            while (chunk < maxChunks) {
                const r = await getAPI(`documents/download/${docID}/${chunk}`, false, true);
                if (!r.response.ok) { toast.error('Error downloading document'); return; }
                data.push(await r.response.arrayBuffer());
                chunk++;
            }
            const combined = new Uint8Array(data.reduce((acc, val) => acc.concat(Array.from(new Uint8Array(val))), []));
            const url = window.URL.createObjectURL(new Blob([combined]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `${fileName}.${extension}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch {
            toast.error('Error downloading document');
        } finally {
            setLoadingDocuments(prev => ({ ...prev, [docID]: false }));
        }
    }

    useEffect(() => {
        if (docId) getDocuments();
    }, [docId]);

    // ── Skeleton ─────────────────────────────────────────────────────────
    function SkeletonList() {
        return (
            <div className="space-y-2">
                {[...Array(4)].map((_, i) => (
                    <div key={i} className={`${themeCSS.cardBg} rounded-xl px-4 py-3.5 flex items-center gap-3`}>
                        <div className={`w-10 h-10 rounded-full ${themeCSS.skeleton} animate-pulse flex-shrink-0`} />
                        <div className="flex-1 space-y-2">
                            <div className={`h-4 rounded-md ${themeCSS.skeleton} animate-pulse w-2/3`} />
                            <div className={`h-3 rounded ${themeCSS.skeleton} animate-pulse w-12`} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{ backgroundColor: themeCSS.pageBg }}>
            <HeaderBackground pageTitle="Documents" />

            <div className="max-w-2xl mx-auto px-4 py-4 pb-28">
                {loading ? (
                    <SkeletonList />
                ) : documents.length === 0 ? (
                    /* Empty state — matches mobile exactly */
                    <div
                        className={`${themeCSS.cardBg} rounded-2xl p-8 flex flex-col items-center text-center`}
                        style={{ border: `2px dashed ${theme === 'dark' ? '#334155' : '#F2F2F7'}` }}
                    >
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-50'}`}>
                            <FileText size={28} className="text-gray-300" strokeWidth={1.5} />
                        </div>
                        <p className={`text-base font-semibold mb-2 ${themeCSS.cardSubtext}`}>No documents found</p>
                        <p className={`text-sm ${themeCSS.cardMuted}`}>Documents shared with you will appear here</p>
                    </div>
                ) : (
                    /* Document list — separate cards with 8px gap, matching mobile */
                    <div className="space-y-2">
                        {documents.map((doc) => {
                            const isDownloading = loadingDocuments[doc.id];
                            return (
                                <button
                                    key={doc.id}
                                    onClick={() => !isDownloading && downloadDocument(doc.id)}
                                    disabled={isDownloading}
                                    className={`w-full ${themeCSS.cardBg} rounded-xl px-4 py-3.5 flex items-center gap-3 text-left shadow-sm ${themeCSS.cardHover} transition-colors disabled:opacity-70`}
                                >
                                    {/* Icon */}
                                    <div
                                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                        style={{ backgroundColor: `${primaryColor}22` }}
                                    >
                                        <FileText size={20} strokeWidth={1.5} style={{ color: primaryColor }} />
                                    </div>

                                    {/* Name + badge */}
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-medium leading-snug ${themeCSS.cardTitle}`}>
                                            {doc.name}
                                        </p>
                                        {doc.file_extension && (
                                            <span
                                                className="inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded"
                                                style={{ color: primaryColor, backgroundColor: `${primaryColor}22` }}
                                            >
                                                {doc.file_extension.toUpperCase()}
                                            </span>
                                        )}
                                    </div>

                                    {/* Chevron / spinner */}
                                    {isDownloading ? (
                                        <div
                                            className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin flex-shrink-0"
                                            style={{ borderColor: `${primaryColor}44`, borderTopColor: primaryColor }}
                                        />
                                    ) : (
                                        <ChevronRight size={20} className={`flex-shrink-0 ${themeCSS.cardMuted}`} strokeWidth={1.5} />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
